import moment from 'moment';
import { v4 as uuidV4 } from 'uuid';
import pick from 'lodash.pick';

import calculateMinimumRestAndMealBreaks from '../minimum-breaks';

// Types WorkPeriodParameters
import {
    WorkPeriodParameters,
    WorkPeriod,
    TotalPaidMinutesParameters,
    TotalPaidMinutes,
    TotalPaidHoursParameters,
    TotalPaidHours,
    TotalPieceRateParameters,
    TotalPieceRate,
    EarningsParameters,
    Earnings,
    EffectiveRateParameters,
    EffectiveRate,
    PaidBreakDollarsParameters,
    PaidBreakDollars,
    Entry,
    EntryForForm,
    EntryForSubmit,
    FormatOptions
} from './types';

export const calculateWorkPeriodMinutes = ({
    startTime,
    endTime
}: WorkPeriodParameters): WorkPeriod => {
    if (!startTime || !endTime) {
        return 0;
    }
    return moment(endTime, 'HH:mm:ss').diff(
        moment(startTime, 'HH:mm:ss'),
        'minutes'
    );
};

export const calculateTotalPaidMinutes = ({
    startTime,
    endTime,
    unpaidBreakMinutes
}: TotalPaidMinutesParameters): TotalPaidMinutes => {
    if (!startTime || !endTime) {
        return 0;
    }
    const totalMinutes = moment(endTime, 'HH:mm:ss').diff(
        moment(startTime, 'HH:mm:ss'),
        'minutes'
    );
    return unpaidBreakMinutes
        ? totalMinutes - unpaidBreakMinutes
        : totalMinutes;
};

export const calculateTotalPaidHours = ({
    startTime,
    endTime,
    unpaidBreakMinutes
}: TotalPaidHoursParameters): TotalPaidHours => {
    if (!startTime || !endTime) {
        return 0;
    }
    const totalMinutes = moment(endTime, 'HH:mm:ss').diff(
        moment(startTime, 'HH:mm:ss'),
        'minutes'
    );
    const totalPaidMinutes = unpaidBreakMinutes
        ? totalMinutes - unpaidBreakMinutes
        : totalMinutes;
    return totalPaidMinutes / 60;
};

export const calculatePieceRateHours = ({
    startTime,
    endTime,
    unpaidBreakMinutes
}: TotalPieceRateParameters): TotalPieceRate => {
    if (!startTime || !endTime) {
        return 0;
    }
    const totalMinutes = moment(endTime, 'HH:mm:ss').diff(
        moment(startTime, 'HH:mm:ss'),
        'minutes'
    );
    const totalPaidMinutes = unpaidBreakMinutes
        ? totalMinutes - unpaidBreakMinutes
        : totalMinutes;
    return totalPaidMinutes / 60;
};

export const calculateEarnings = ({
    units,
    rate
}: EarningsParameters): Earnings => {
    if (units && rate) {
        return units * rate;
    }

    return undefined;
};

export const calculateEffectiveRate = ({
    earnings,
    totalPieceRateHours
}: EffectiveRateParameters): EffectiveRate => {
    if (earnings && totalPieceRateHours) {
        return earnings / totalPieceRateHours;
    }

    return undefined;
};

export const calculatePaidBreakDollars = ({
    effectiveRate,
    paidBreakMinutes
}: PaidBreakDollarsParameters): PaidBreakDollars => {
    if (effectiveRate && paidBreakMinutes) {
        return effectiveRate * (paidBreakMinutes / 60);
    }

    return undefined;
};

const formFieldWhitelist = ['date', 'timesheetId', 'lastReviewIsApproved'];

// TODO: remove any
export const formatEntryForForm = (value: Entry): any => {
    const {
        uuid,
        startTime,
        endTime,
        paidBreakMinutes,
        unpaidBreakMinutes,
        units,
        rate,
        workTypeCompositeKey,
        entityCompositeKey,
        personFirstName,
        personLastName,
        personId,
        hasEditedPaidBreak,
        hasEditedUnpaidBreak
    } = value;

    let isPieceRate;
    let workTypeEntityTypeId;
    let entityEntityTypeId;

    if (workTypeCompositeKey) {
        const [
            ,
            isPieceRateString,
            workTypeEntityTypeIdString
        ] = workTypeCompositeKey.split('.');
        isPieceRate = isPieceRateString
            ? isPieceRateString === 'true'
            : undefined;
        workTypeEntityTypeId = workTypeEntityTypeIdString
            ? Number(workTypeEntityTypeIdString)
            : undefined;
    }

    if (entityCompositeKey) {
        const [, entityEntityTypeIdString] = entityCompositeKey.split('.');
        entityEntityTypeId = entityEntityTypeIdString
            ? Number(entityEntityTypeIdString)
            : undefined;
    }

    const workPeriodMinutes = calculateWorkPeriodMinutes({
        startTime,
        endTime
    });
    const {
        totalMealBreaksMinutes,
        totalRestBreaksMinutes
    } = calculateMinimumRestAndMealBreaks(workPeriodMinutes);

    let nextPaidBreakMinutes;
    const nextUnpaidBreakMinutes = hasEditedUnpaidBreak
        ? unpaidBreakMinutes
        : totalMealBreaksMinutes;

    let calculatedFields;
    if (isPieceRate) {
        nextPaidBreakMinutes = hasEditedPaidBreak
            ? paidBreakMinutes
            : totalRestBreaksMinutes || null;

        const totalPieceRateHours = calculatePieceRateHours({
            startTime,
            endTime,
            unpaidBreakMinutes
        });
        const earnings = calculateEarnings({
            units,
            rate
        });
        const effectiveRate = calculateEffectiveRate({
            earnings,
            totalPieceRateHours
        });
        const paidBreakDollars = calculatePaidBreakDollars({
            effectiveRate,
            paidBreakMinutes
        });

        calculatedFields = {
            totalPieceRateHours: totalPieceRateHours || null,
            earnings: earnings || null,
            effectiveRate: effectiveRate || null,
            paidBreakDollars: paidBreakDollars || null,
            units,
            rate
        };
    } else {
        const totalPaidHours = calculateTotalPaidHours({
            startTime,
            endTime,
            unpaidBreakMinutes
        });

        calculatedFields = {
            totalPaidHours: totalPaidHours || null,
            rate: null,
            paidBreakMinutes: null,
            units: units || null
        };
    }

    return {
        ...pick(value, formFieldWhitelist),
        uuid: uuid || uuidV4(),
        startTime,
        endTime,
        totalPaidMinutes: calculateTotalPaidMinutes({
            startTime,
            endTime,
            unpaidBreakMinutes
        }),
        paidBreakMinutes: nextPaidBreakMinutes,
        unpaidBreakMinutes: nextUnpaidBreakMinutes || null,
        workTypeCompositeKey,
        personId,
        personFirstName,
        personLastName,
        personFullName: personId
            ? `${value.personFirstName ? `${value.personFirstName} ` : ''}${
                  value.personLastName
              }`
            : undefined,
        entityCompositeKey:
            workTypeEntityTypeId && workTypeEntityTypeId !== entityEntityTypeId
                ? null
                : entityCompositeKey,
        hasEditedPaidBreak,
        hasEditedUnpaidBreak,
        ...calculatedFields
    };
};

// Whitelist of fields accepted by API
// TODO: add manualPaidBreakMinutes, manualUnpaidBreakMinutes
const submitFieldWhitelist = [
    'uuid',
    'personId',
    'date',
    'startTime',
    'endTime',
    'rate',
    'units',
    'paidBreakMinutes',
    'unpaidBreakMinutes',
    'hasEditedPaidBreak',
    'hasEditedUnpaidBreak'
];

// TODO: remove any
export const formatEntryForSubmit = (
    value: EntryForForm,
    options: FormatOptions = {}
): EntryForSubmit => {
    const { timesheetId, workTypeCompositeKey, entityCompositeKey } = value;
    return {
        ...pick(value, submitFieldWhitelist),
        timesheetId: options.excludeTimesheetId ? undefined : timesheetId,
        workTypeId: workTypeCompositeKey
            ? Number(workTypeCompositeKey.split('.')[0])
            : undefined,
        entityId: entityCompositeKey
            ? Number(entityCompositeKey.split('.')[0])
            : undefined
    };
};
